import { IEventLoggingComponentProps, withEventLogging } from '@AppModels/Logging';
import { IOperationResult, IOperationResultNoValue, ISystemJob, TagOwnerReportSystemJobViewModel } from '@ViewModels';
import { css } from 'aphrodite';
import { inject, observer } from 'mobx-react';
import * as React from 'react';
import {
	ErrorMessagesViewModelKey,
	IErrorMessageComponentProps,
	IUserSessionComponentProps,
	UserSessionViewModelKey,
} from '../../../../../models/AppState';
import { baseStyleSheet } from '../../../../styles/styles';
import { LoadingSpinner } from '../../../LoadingSpinner';
import { asModalComponent } from '../../../Modal';
import { SvgIcon } from '../../../svgs/icons/SvgIcon';
import { styleSheet } from './styles';

interface IProps extends IEventLoggingComponentProps, IUserSessionComponentProps, IErrorMessageComponentProps {
	accountId?: string;
	className?: string;
	title?: string;
}

class _GenerateTagNotificationsReport extends React.Component<IProps> {
	private mTagOwnerReportSystemJobViewModel: TagOwnerReportSystemJobViewModel;

	private mMounted: boolean;

	constructor(props: IProps) {
		super(props);
		this.mTagOwnerReportSystemJobViewModel = new TagOwnerReportSystemJobViewModel(
			props.userSession,
			null,
			props.accountId
		);
	}

	public componentDidMount() {
		this.mMounted = true;
		const promise = this.mTagOwnerReportSystemJobViewModel.runReport();
		const { logEvent, logApiError, errorMessages } = this.props;
		if (promise) {
			logEvent('RunReport');
			promise
				.then(() => {
					if (this.mMounted) {
						this.mTagOwnerReportSystemJobViewModel.startWatching(this.continueWatching);
					}
				})
				.catch((error: IOperationResultNoValue) => {
					logApiError('RunReport-Error', error);

					errorMessages.pushApiError(error);
				});
		}
	}

	public componentWillUnmount() {
		this.mMounted = false;
		this.mTagOwnerReportSystemJobViewModel.stopWatching();
	}

	public render() {
		const { className } = this.props;
		return (
			<div className={`${css(styleSheet.container)} generate-tag-notifications-report ${className || ''}`}>
				<div className={css(styleSheet.header)}>{this.props.title || 'Tag Notifications Report'}</div>
				<div className={css(styleSheet.body)}>
					<SvgIcon className={css(styleSheet.icon)} height={102} width={78}>
						<defs>
							<path id='path-b' d='M0,0 L70,0 L70,94 L0,94 L0,0 Z' />
							<filter id='path-a' height='114.9%' y='-5.3%' filterUnits='objectBoundingBox'>
								<feOffset dy='2' in='SourceAlpha' result='shadowOffsetOuter1' />
								<feGaussianBlur in='shadowOffsetOuter1' result='shadowBlurOuter1' stdDeviation='2' />
								<feColorMatrix
									in='shadowBlurOuter1'
									values='0 0 0 0 0.669327446   0 0 0 0 0.669327446   0 0 0 0 0.669327446  0 0 0 0.5 0'
								/>
							</filter>
						</defs>
						<g fill='none' fillRule='evenodd' transform='translate(4 2)'>
							<use fill='#000' filter='url(#path-a)' xlinkHref='#path-b' />
							<use fill='#F9FDFF' xlinkHref='#path-b' />
							<path
								fill='#D3DBDE'
								d='M19.5 68L47.5 68C48.3284271 68 49 68.6715729 49 69.5L49 69.5C49 70.3284271 48.3284271 71 47.5 71L19.5 71C18.6715729 71 18 70.3284271 18 69.5L18 69.5C18 68.6715729 18.6715729 68 19.5 68zM19.5 74L41.5 74C42.3284271 74 43 74.6715729 43 75.5L43 75.5C43 76.3284271 42.3284271 77 41.5 77L19.5 77C18.6715729 77 18 76.3284271 18 75.5L18 75.5C18 74.6715729 18.6715729 74 19.5 74z'
							/>
							<rect width='6' height='37' x='18' y='20' fill='#DBF2C3' />
							<rect width='6' height='29' x='28' y='28' fill='#DBF2C3' />
							<rect width='6' height='26' x='38' y='31' fill='#DBF2C3' />
							<rect width='6' height='34' x='48' y='23' fill='#89C947' />
						</g>
					</SvgIcon>
					<div>
						{this.mTagOwnerReportSystemJobViewModel.ownerReportPdfDownloadUrl
							? 'Your report is ready for download.'
							: 'We are preparing your report...'}
					</div>
				</div>
				<div className={css(styleSheet.footer)}>
					{this.mTagOwnerReportSystemJobViewModel.ownerReportPdfDownloadUrl ? (
						<a
							className={`${css(baseStyleSheet.ctaButton, styleSheet.downloadButton)}`}
							href={this.mTagOwnerReportSystemJobViewModel.ownerReportPdfDownloadUrl}
							onClick={this.onDownloadLinkClicked}
							target='_blank'
							rel='noreferrer'
						>
							<span>Download</span>
						</a>
					) : (
						<LoadingSpinner type='small' />
					)}
				</div>
			</div>
		);
	}

	private onDownloadLinkClicked = () => {
		this.props.logInput('Download', 'Click');
	};

	private continueWatching = (opResult: IOperationResult<ISystemJob>) => {
		const { logEvent, logApiError, errorMessages } = this.props;
		if (!opResult.success) {
			logApiError('GetSystemJobUpdate-Error', opResult);

			errorMessages.pushApiError(opResult);
			return false;
		}

		if (opResult.value.percentComplete >= 100) {
			const promise = this.mTagOwnerReportSystemJobViewModel.getReportPdfDownloadUrl();
			if (promise) {
				logEvent('GetDownloadLink');
				promise.catch((error: IOperationResultNoValue) => {
					logApiError('GetDownloadLink-Error', error);

					errorMessages.pushApiError(error);
				});
			}
			return false;
		}
		return true;
	};
}

const GenerateTagNotificationsReportAsObserver = observer(_GenerateTagNotificationsReport);
const GenerateTagNotificationsReportWithContext = inject(
	UserSessionViewModelKey,
	ErrorMessagesViewModelKey
)(GenerateTagNotificationsReportAsObserver);
export const GenerateTagNotificationsReport = withEventLogging(
	GenerateTagNotificationsReportWithContext,
	'GenerateTagNotificationsReport'
);
export const GenerateTagNotificationsReportModal = asModalComponent(GenerateTagNotificationsReport, {
	className: 'GenerateTagNotificationsReportModal',
	shouldCloseOnOverlayClick: false,
	useDefaultHeader: true,
});
